<template>
  <component
      :is="$device.isMobile ? 'sliding-modal' : 'div'"
      v-bind="rootProps"
      :selenium="isCollapsed ? 'messages-collapsed' : ''">
    <component
        :is="$device.isMobile ? 'vk-modal-body' : 'div'"
        v-bind="bodyProps"
        selenium="messages-container">
      <component
          :is="$device.isMobile ? 'vk-modal-header' : 'div'"
          class="HEADER"
          selenium="messages-header"
          :class="{ 'x-pulse': isFetching && !isCreating }"
          @click="() =>
            $device.isNeitherMobileOrTablet
            && $router.replace(getMessagesLink(isCollapsed ? 'messages' : undefined))
          ">
        <router-link
            v-if="$device.isMobile"
            class="ICON"
            replace
            :to="getMessagesLink()">
          <vk-icon name="arrow-left" class="h4" />
        </router-link>
        Message {{ job.location.first_name }}
        <transition v-if="!$device.isMobile" name="FADE" mode="out-in">
          <router-link
              v-if="isCollapsed"
              class="ICON"
              key="minus"
              replace
              :event="$device.isTablet ? 'click' : ''"
              :to="getMessagesLink('messages')">
            <vk-icon name="minus-padded" class="h4" />
          </router-link>
          <router-link
              v-else
              class="ICON"
              key="close"
              replace
              selenium="messages-close"
              :event="$device.isTablet ? 'click' : ''"
              :to="getMessagesLink()">
            <vk-icon name="close-slim" class="h4" />
          </router-link>
        </transition>
      </component>
      <div class="SUB-HEADER" selenium="messages-subheader">
        <vk-icon name="lightbulb-outlined" class="h4 flex-0-0 m-r-1 c-yellow" />
        <span>
          Customer will receive your message as an
          <strong>email</strong> and <strong>not a text</strong>
        </span>
      </div>
      <transition-group
          name="FADE"
          tag="div"
          appear
          v-infinite-scroll.reverse="{
            offset: 666,
            promise: currentJobMessagesMeta.hasMore
              && (!isFetching || !isCreating)
              && loadMoreMessages
          }"
          class="MESSAGES-LIST-CONTAINER">
        <transition-group
            name="FADE"
            tag="div"
            v-for="(rawMessages, group) in
            getGroupedByDateCurrentRawMessagesByJobId(job.order_hash)"
            :key="group"
            class="GROUP">
          <div
              class="GROUP-HEADING"
              key="group"
              selenium="messages-group">
            {{ new Date(group) | timeReceivedDefault }}
          </div>
          <transition-group
              name="FADE-AND-MOVE"
              tag="div"
              key="content"
              class="d-flex fd-column-reverse">
            <message-bubble
                v-for="message in rawMessages"
                :key="message.message_id"
                :message="message"
                :isCollapsed="isCollapsed"
                class="LIST-ITEM"
            />
          </transition-group>
        </transition-group>
      </transition-group>
      <div
          v-if="hasFiles || isUploading"
          class="FILES-CONTAINER"
          selenium="uploaded-files"
          :class="{ 'x-pulse': isUploading, 'DISABLED': isUploading || isMessagingDisabled }">
        <gallery :files="newFiles" deletable class="FILES" @deleteImage="onFileDelete" />
      </div>
      <div
          v-if="hasCurrentJobMessagesError || hasCurrentJobFilesError || hasNewMessageError"
          class="ERROR"
          selenium="messages-error">
        <error-messages
            v-if="hasCurrentJobFilesError"
            :error="currentJobFilesMeta.error"
            default-message="Failed to upload your attachment. Please try again later..." />
        <error-messages
            v-if="hasCurrentJobMessagesError"
            :class="{ 'm-t-1': hasCurrentJobFilesError }"
            :error="currentJobMessagesMeta.error"
            default-message="Failed to send your message. Please try again later..." />
        <error-messages
            v-if="hasNewMessageError"
            :class="{ 'm-t-1': hasCurrentJobMessagesError }"
            :error="validation.firstError('newMessage')" />
      </div>
      <div class="TEXTAREA-CONTAINER">
        <label
            class="ATTACH"
            :class="{ 'DISABLED': isMessagingDisabled }"
            selenium="messages-attach">
          <vk-icon name="picture" />
          <input
              :disabled="isMessagingDisabled"
              accept="image/*"
              :size="$options.DEFAULT_MAX_IMAGE_SIZE"
              type="file"
              multiple
              class="HIDDEN"
              selenium="message-upload-input"
              @change="onNewFilesUpload"
          />
        </label>
        <textarea-input
            :disabled="isMessagingDisabled"
            ref="textarea"
            v-model="newMessage"
            :placeholder="`Message ${job.location.first_name}...`"
            class="TEXTAREA"
            selenium="text-input"
            @keypress.native.enter="onNewMessageCreateWithShiftPlusEnter"
        />
        <a
            class="SEND"
            selenium="send-button"
            :class="{ 'DISABLED': isSendingDisabled }"
            @click.prevent="onNewMessageCreate">
          <vk-icon name="paperplane" />
        </a>
      </div>
      <image-popup
          :isOpen="isShowImagePopup"
          @close= "isShowImagePopup=false"/>
    </component>
  </component>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { shouldBeDisabled } from '@homeservices/tool-kit/proreferral/utils/messages';
import SimpleVueValidator, { Validator } from 'simple-vue-validator';
import { VALID_IMAGE_ACCEPT_TYPES, DEFAULT_MAX_IMAGE_SIZE } from '@/constants';
import http from '@homeservices/api-kit/axios';
import jobParams from '@/mixins/job-params';
import job from '@/mixins/job';
import messages from '@/mixins/job/messages'; // TODO - messages, this should be removed as it is no longer correct
import files from '@/mixins/job/files';
import SlidingModal from '@/views/Dashboard/components/SlidingModal';
import ErrorMessages from '@/views/Dashboard/components/ErrorMessages';
import TextareaInput from '@/views/Dashboard/components/TextareaInput';
import passEducation from '@/views/Dashboard/mixins/pass-education';
import ImagePopup from '@/components/ServicesPricing/ImagePopup';

import { SUB_STATUS_OPTIONS } from '@/constants/sub-status-options';
import MessageBubble from './MessageBubble';

export default {
  VALID_IMAGE_ACCEPT_TYPES,
  DEFAULT_MAX_IMAGE_SIZE,
  SUB_STATUS_OPTIONS,
  shouldBeDisabled,
  name: 'pro-messages',
  components: {
    SlidingModal,
    TextareaInput,
    MessageBubble,
    ErrorMessages,
    ImagePopup,
  },
  mixins: [
    SimpleVueValidator.mixin,
    passEducation,
    jobParams,
    job,
    messages,
    files,
  ],
  data () {
    return {
      newFiles: [],
      newMessage: '',
      isCreating: false,
      isUploading: false,
      isShowImagePopup: false,

    };
  },
  computed: {
    ...mapGetters('account/pro/info', [
      'serviceProviderId',
    ]),
    rootProps () {
      return this.$device.isMobile ? {
        isOpen: !this.isCollapsed,
        transitionName: 'SLIDE-RIGHT',
        backdropClass: 'bg-transparent',
      } : {
        class: ['MESSAGES', { COLLAPSED: this.isCollapsed }],
      };
    },
    bodyProps () {
      return this.$device.isMobile ? {
        slot: 'body',
        class: 'SHADOW',
      } : {
        class: 'MESSAGES-CONTAINER SHADOW',
      };
    },
    isCollapsed () {
      return this.$route.query.feature !== 'messages';
    },
    isFetching () {
      return (
        this.currentJobMessagesMeta.status
        === this.$options.REQUEST_LIFECYCLE.FETCHING
      );
    },
    isMessagingDisabled () {
      return (
        this.isFetching
        || this.isCreating
        || shouldBeDisabled(this.match.status)
      );
    },
    isSendingDisabled () {
      return (
        (!this.newMessage && !this.hasFiles)
        || this.hasNewMessageError
        || this.isUploading
        || this.isMessagingDisabled
      );
    },
    hasFiles () {
      return this.newFiles.length;
    },
    hasCurrentJobMessagesError () {
      return this.currentJobMessagesMeta.error;
    },
    hasCurrentJobFilesError () {
      return this.currentJobFilesMeta.error;
    },
    hasNewMessageError () {
      return this.validation.hasError('newMessage');
    },
  },
  validators: {
    newMessage (value) {
      return Validator.value(value).maxLength(1024, `Message text is too long (${value.length}), maximum is 1024 characters`);
    },
  },
  watch: {
    jobId () {
      this.$router.replace(this.getMessagesLink());
      this.clearMessages();
      this.loadMessages();
      this.reconcile();
    },
    isCollapsed () {
      if (!this.isCollapsed && !this.$device.isMobile) {
        global.setTimeout(() => this.$refs.textarea.$el.focus(), 300);
      }
    },
  },
  mounted () {
    this.clearMessages();
    this.loadMessages();
    this.$validate();
  },
  methods: {
    ...mapActions('account/pro', ['updatePendo']),

    getMessagesLink (feature) {
      return {
        name: 'dashboard::pro::jobs',
        params: this.$route.params,
        query: {
          ...this.$route.query,
          feature,
        },
      };
    },
    reconcile () {
      this.newMessage = '';
      this.newFiles = [];
    },
    async loadMessages (params) {
      await this.fetchMessages({
        jobId: this.job.order_hash,
        params: {
          ...params,
        },
      });
    },
    async loadMoreMessages () { // THIS APPEARS TO BE AN EXISTING ISSUE (DOES NOT WORK WITH OLD API)
      await this.loadMessages({ from: this.currentJobMessagesMeta.count });
    },
    clearMessages () {
      this.resetCurrentMessages();
    },
    onNewMessageCreateWithShiftPlusEnter (event) {
      if (event.shiftKey) {
        event.preventDefault();

        this.onNewMessageCreate();
      }
    },
    async uploadFile (file) {
      const body = new FormData();

      body.append('file', file);

      const data = await this.createFiles({
        jobId: this.job.order_hash,
        skillsProfile: this.serviceProviderId,
        body,
      });

      this.newFiles = [...this.newFiles, data];
    },
    async onNewFilesUpload ({ target }) {
      this.isUploading = true;

      try {
        if (target.files.length <= 8) {
          await Promise.all(Array.from(target.files).map(this.uploadFile));
        } else {
          this.isShowImagePopup = true;
        }
      // eslint-disable-next-line no-empty
      } catch (error) {} finally {
        this.isUploading = false;
      }
    },
    async onFileDelete (fileId) {
      await this.deleteFiles({
        jobId: this.job.order_hash,
        skillsProfile: this.serviceProviderId,
        fileId,
      });

      this.newFiles = this.newFiles.filter(({ id }) => id !== fileId);
    },
    async onNewMessageCreate () {
      if (this.isSendingDisabled) {
        return;
      }

      this.resetCurrentFilesError();

      this.isCreating = true;

      try {
        await this.createMessages({
          jobId: this.job.order_hash,
          params: {
            message: this.newMessage,
            asset_urls: this.newFiles.map(({ url }) => url),
          },
        });

        this.reconcile();

        this.$events.$emit(this.$events.PRO_SEND_MESSAGE, {
          jobID: this.job.order_hash,
        });

        if (this.isNewStatusAlias || (this.isNewMatch && this.isSearchStatusAlias)) {
          await this.transitionJob({
            action: this.$options.MATCH_STATUS_ACTIONS.CALL_CONSUMER,
            from: this.$options.JOBS_STATUS_ALIASES.new.key,
            to: this.$options.JOBS_STATUS_ALIASES.potential.key,
            order_hash: this.job.order_hash,
          });
          await http.put(`/platform/workorder/r/workorder/service-provider-sub-status/v1?order_hash=${this.job.order_hash}&service_provider_id=${this.serviceProviderId}&service_provider_status=potential&service_provider_sub_status=${this.$options.SUB_STATUS_OPTIONS.messaged_in_application.text}`);
          this.$events.$emit(
            this.$events.SUB_STATUS_INITIAL, {
              substatus: this.$options.SUB_STATUS_OPTIONS.messaged_in_application.sub_status_name,
            }
          );

          this.$router.replace({
            name: 'dashboard::pro::jobs',
            params: {
              jobsStatusAlias: this.$options.JOBS_STATUS_ALIASES.potential.key,
              jobId: this.job.order_hash,
            },
            query: this.$route.query,
          });

          this.$fireToast({
            message: this.$options.getMatchStatusActionsMessage({
              action: this.$options.MATCH_STATUS_ACTIONS.CALL_CONSUMER,
              firstName: this.job.location.first_name,
            }),
          });

          this.$events.$emit(this.$events.PRO_CHANGE_STATUS, {
            jobID: this.job.id,
            changeTo: this.$options.JOBS_STATUS_ALIASES.potential.key,
          });

          this.resetBusyPassCount();
          this.updatePendo('update');
        }
      } catch (error) {
        this.$events.$emit(this.$events.PRO_SEND_MESSAGE_ERROR, {
          jobID: this.job.order_hash,
        });
      } finally {
        this.isCreating = false;

        if (!this.$device.isMobile) {
          global.setTimeout(() => this.$refs.textarea.$el.focus(), 150);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  @import "~@/views/Dashboard/constants";
  @import "~@/views/Dashboard/tools";

  .MESSAGES {
    @include tablet {
      z-index: 1;
    }

    @include desktop {
      position: sticky;
      bottom: 0;
      width: 100%;
    }

    &.COLLAPSED .MESSAGES-CONTAINER {
      @include responsive(
        transform,
        none,
        translateX(103%),
        translateY(calc(100% - 56px))
      );
    }
  }

  .MESSAGES-CONTAINER {
    display: flex;
    flex-direction: column;
    background-color: white;
    overflow-y: hidden;
    transform: none;
    will-change: transform;
    transition: transform $transition-duration;

    @include tablet {
      @include absolute(0, 0, 0, 0);
    }

    @include desktop {
      @include absolute(auto, 5*$unit, 0, auto);
      width: 390px;
      height: 500px;
      border: 1px solid $color-gray30;
    }
  }

  .SHADOW {
    @include responsive(box-shadow, 0 0 10px rgba(black, 0.2), $md: none);
    backface-visibility: hidden;
  }

  .HEADER {
    @include padding(0 5, 2 7);
    position: relative;
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    text-align: center;
    background-color: $color-depot;
    color: white;
    font-weight: 700;
    cursor: pointer;
    border-bottom: none;
    backface-visibility: hidden;
  }

  .SUB-HEADER {
    @include text-medium;
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    padding: 0.75*$unit 2*$unit;
    background-color: $color-gray10;
    border-bottom: 1px solid $color-gray30;
    color: $color-gray70;
  }

  .ICON {
    @include absolute(50%, auto, auto, 2*$unit);
    @include square(3*$unit);
    @include desktop-hover(opacity, 0.7, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    transform: translateY(-50%);
    will-change: opacity;
    transition: opacity $transition-half-duration;

    @include tablet {
      @include absolute(50%, 2*$unit, auto, auto);
    }
  }

  .MESSAGES-LIST-CONTAINER {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column-reverse;
    padding: $unit 2*$unit;
    overflow-y: scroll;
  }

  .GROUP {
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
  }

  .GROUP-HEADING {
    @include text-small;
    position: sticky;
    display: flex;
    align-self: center;
    top: $unit;
    margin: $unit 0;
    padding: $unit/4 $unit;
    color: white;
    font-weight: 600;
    background-color: $color-gray40;
    border-radius: 3px;
    z-index: 1;
  }

  .FILES-CONTAINER {
    display: flex;
    flex: 0 0 auto;
    flex-direction: row-reverse;
    height: 103px;
    padding: 2*$unit;
    border-top: 1px solid $color-gray30;
    overflow-x: scroll;

    // https://www.geeksforgeeks.org/hide-scroll-bar-but-while-still-being-able-to-scroll-using-css/
    // https://developer.mozilla.org/en-US/docs/Web/CSS/scrollbar-width
    scrollbar-width: none;
    overflow: -moz-scrollbars-none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      width: 0 !important;
      height: 0 !important;
    }

    &.DISABLED {
      pointer-events: none;
      opacity: 0.3;
    }
  }

  .FILES {
    flex-wrap: nowrap;
    overflow: visible !important;
  }

  .ERROR {
    @include text-medium;
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    padding: $unit 2*$unit;
    background-color: $color-red;
    color: white;
  }

  .TEXTAREA-CONTAINER {
    position: relative;
    display: flex;
    flex: 0 0 auto;
  }

  .HIDDEN {
    display: none;
  }

  .TEXTAREA {
    display: flex;
    min-height: 57px;
    max-height: 153px;
    padding: 2*$unit 7*$unit;
    border: none;
    border-top: 1px solid $color-gray30;
    will-change: height, opacity;
    transition: height $transition-half-duration,
      opacity $transition-half-duration;
    backface-visibility: hidden;
    overflow-y: scroll;
  }

  .SEND,
  .ATTACH {
    @include square(24px);
    @include h4;
    @include absolute(50%, 2*$unit, auto, auto);
    display: flex;
    transform: translateY(-50%);
    align-items: center;
    justify-content: center;
    z-index: 2;
    will-change: color, opacity;
    transition: color $transition-half-duration, opacity $transition-half-duration;
  }

  .SEND {
    @include link-primary;
  }

  .ATTACH {
    @include link-black;
    left: 2*$unit;
    right: auto;
    z-index: 2;
  }

  .DISABLED {
    @include hover(color, $color-gray40, $color-gray40);
    color: $color-gray40;
    cursor: not-allowed;
  }

  .LIST-ITEM {
    transition: all $transition-half-duration;
  }

  .FADE-AND-MOVE-enter-active,
  .FADE-AND-MOVE-leave-active {
    opacity: 1;
    will-change: opacity;
    transition: opacity $transition-half-duration;

    &.FADE-AND-MOVE-enter,
    &.FADE-AND-MOVE-leave-to {
      opacity: 0;
    }
  }

  .FADE-AND-MOVE-leave-active {
    @include absolute;
  }
</style>
